import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, Subject } from 'rxjs';
import { map, catchError, switchMap, withLatestFrom, tap, filter, mergeMap, take, takeUntil } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { fromCustomImageActions } from './custom-image.actions';
import { fromCustomImageSelectors } from './custom-image.selectors';
import { DesignState } from '../design.reducer';
import { CustomImage, CustomImageImpl } from '@design/models/custom-image.model';
import { openNotificationService } from '@lib-ui/lib/atomic/enact-alert-snack-bar/openNotification.service';
import { NotificationType } from '@lib-ui/lib/atomic/enact-alert-snack-bar/notification.message';
import { QuotesDetailsApi } from '@design/api/quotes-details-api';
import { TranslateService } from '@ngx-translate/core';
import { fromUiActions } from '../ui';
import { fromRoofSectionActions } from '../roof-section';
import { CustomImageCalibrationDialogComponent, CustomImageCalibrationDialogResult } from '@design/components/custom-image-calibration-dialog/custom-image-calibration-dialog.component';
import { fromMetaDataSelectors } from '../meta-data';
import { MeasurementUnits } from '@design/models/preferences.model';

// File size limit: 15MB
const MAX_FILE_SIZE = 15 * 1024 * 1024;
const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

@Injectable()
export class CustomImageEffects {
  private calibrationDialogRef: MatDialogRef<CustomImageCalibrationDialogComponent, CustomImageCalibrationDialogResult> | null = null;
  private cancelCalibrationSubject$ = new Subject<void>();

  constructor(
    private actions$: Actions,
    private store: Store<DesignState>,
    private notificationService: openNotificationService,
    private quotesApi: QuotesDetailsApi,
    private dialog: MatDialog,
    private translate: TranslateService
  ) {}

  uploadCustomImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCustomImageActions.UploadCustomImage),
      switchMap(({ file, uploadSourceType }) => {
        // Validate file type
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
          return of(fromCustomImageActions.UploadCustomImageFailure({
            error: this.translate.instant('customImage.invalidFileType') || 'Invalid file type. Please upload PNG, JPG, or JPEG.'
          }));
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
          return of(fromCustomImageActions.UploadCustomImageFailure({
            error: this.translate.instant('customImage.fileTooLarge') || 'File size exceeds 15MB limit.'
          }));
        }

        return new Promise<CustomImage>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
              const customImage = new CustomImageImpl({
                imageUrl: e.target?.result as string,
                originalWidthPx: img.width,
                originalHeightPx: img.height,
                position: { x: 0, y: 0, z: -4 },
                rotation: 0,
                opacity: 70,
                visible: true,
                scale: {
                  width: img.width,
                  height: img.height,
                  aspectRatio: img.width / img.height
                },
                calibrated: false,
                uploadSourceType
              });
              resolve(customImage);
            };
            img.src = e.target?.result as string;
          };
          reader.readAsDataURL(file);
        }).then((customImage) => fromCustomImageActions.UploadCustomImageSuccess({ customImage }));
      }),
      catchError((error) => of(fromCustomImageActions.UploadCustomImageFailure({ error: error.message })))
    )
  );

  uploadCustomImageFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCustomImageActions.UploadCustomImageFailure),
      tap(({ error }) => {
        this.notificationService.sendMessage({
          message: error,
          type: NotificationType.error,
          positionClass: 'toast-bottom-left',
          timeOut: 5000
        });
      })
    ),
    { dispatch: false }
  );

  deleteCustomImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCustomImageActions.DeleteCustomImage),
      withLatestFrom(this.store.select(fromCustomImageSelectors.selectCustomImage)),
      mergeMap(([_, customImage]) => {
        // Note: We keep the drawn designs when deleting the image per PRD
        // Dispatch both DeleteCustomImageSuccess and SaveRoofDesign to persist the change
        // This ensures customImage: null is saved to the design, preventing restoration on reload
        return [
          fromCustomImageActions.DeleteCustomImageSuccess(),
          fromRoofSectionActions.SaveRoofDesign()
        ];
      })
    )
  );

  beginCalibration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCustomImageActions.BeginCalibration),
      withLatestFrom(this.store.select(fromMetaDataSelectors.selectPreference)),
      switchMap(([{ customImage }, preferences]) => {
        // Get measurement unit from preferences
        let measurementUnit: 'ft' | 'm' = 'ft';
        if (preferences && preferences.measurementUnit) {
          const unit = preferences.measurementUnit.charAt(0).toUpperCase() + preferences.measurementUnit.slice(1);
          measurementUnit = unit === MeasurementUnits.METER ? 'm' : 'ft';
        }
        
        // Open dialog immediately when scale mode starts (with 0 points)
        // Dialog input will be disabled until 2 points are placed
        console.log('[CustomImageEffects] Calibration begun for image:', customImage.id);
        return of(fromCustomImageActions.OpenCalibrationDialog({
          customImage: customImage,
          pointsCount: 0
        }));
      })
    )
  );

  // Open calibration dialog after first point is placed
  openCalibrationDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCustomImageActions.OpenCalibrationDialog),
      withLatestFrom(this.store.select(fromMetaDataSelectors.selectPreference)),
      switchMap(([{ customImage, pointsCount }, preferences]) => {
        // Get measurement unit from preferences
        let measurementUnit: 'ft' | 'm' = 'ft';
        if (preferences && preferences.measurementUnit) {
          const unit = preferences.measurementUnit.charAt(0).toUpperCase() + preferences.measurementUnit.slice(1);
          measurementUnit = unit === MeasurementUnits.METER ? 'm' : 'ft';
        }

        // Close existing dialog if any
        if (this.calibrationDialogRef) {
          this.calibrationDialogRef.close();
        }
        
        // Reset the cancel subject
        this.cancelCalibrationSubject$ = new Subject<void>();

        this.calibrationDialogRef = this.dialog.open(CustomImageCalibrationDialogComponent, {
          width: 'auto',
          panelClass: 'custom-image-calibration-dialog-container',
          disableClose: true,
          hasBackdrop: false,
          position: {
            bottom: '40px'
          },
          data: {
            pixelDistance: customImage.calibration?.pixelsDistance || 0,
            point1: customImage.calibration?.point1 || null,
            point2: customImage.calibration?.point2 || null,
            measurementUnit: measurementUnit,
            pointsCount: pointsCount
          }
        });

        return this.calibrationDialogRef.afterClosed().pipe(
          takeUntil(this.cancelCalibrationSubject$),
          withLatestFrom(this.store.select(fromCustomImageSelectors.selectCustomImage)),
          switchMap(([result, latestCustomImage]: [CustomImageCalibrationDialogResult | undefined, CustomImage | null]) => {
            this.calibrationDialogRef = null;
            
            if (result && result.confirmed && result.actualDistance > 0 && latestCustomImage) {
              // User confirmed with a valid distance - calibrate the image
              if (latestCustomImage.calibration?.point1 && latestCustomImage.calibration?.point2) {
                return of(fromCustomImageActions.CalibrateCustomImage({
                  customImage: latestCustomImage,
                  point1: latestCustomImage.calibration.point1,
                  point2: latestCustomImage.calibration.point2,
                  actualDistance: result.actualDistance
                }));
              }
            }
            // User cancelled - cancel calibration
            return of(fromCustomImageActions.CancelCalibration());
          })
        );
      })
    )
  );

  // Close dialog when calibration is cancelled
  cancelCalibration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCustomImageActions.CancelCalibration),
      tap(() => {
        if (this.calibrationDialogRef) {
          this.calibrationDialogRef.close();
          this.calibrationDialogRef = null;
        }
        this.cancelCalibrationSubject$.next();
      })
    ),
    { dispatch: false }
  );

  calibrateCustomImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCustomImageActions.CalibrateCustomImage),
      map(({ customImage, point1, point2, actualDistance }) => {
        // Calculate pixel distance between points
        const pixelDistance = Math.sqrt(
          Math.pow(point2.x - point1.x, 2) +
          Math.pow(point2.y - point1.y, 2) +
          Math.pow(point2.z - point1.z, 2)
        );

        // Calculate scale factor
        const scaleFactor = actualDistance / pixelDistance;
        
        // Calculate new dimensions
        const newWidth = customImage.scale.width * scaleFactor;
        const newHeight = customImage.scale.height * scaleFactor;

        const calibratedImage = new CustomImageImpl({
          ...customImage,
          scale: {
            width: newWidth,
            height: newHeight,
            aspectRatio: newWidth / newHeight
          },
          calibrated: true,
          calibration: {
            isCalibrated: true,
            point1,
            point2,
            actualDistance: actualDistance,
            actualDistanceFt: actualDistance,
            pixelsDistance: pixelDistance,
            scaleFactor: scaleFactor,
            scaleFactorApplied: scaleFactor
          }
        });

        return fromCustomImageActions.CalibrateCustomImageSuccess({ customImage: calibratedImage });
      })
    )
  );

  calibrateCustomImageSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCustomImageActions.CalibrateCustomImageSuccess),
      tap(({ customImage }) => {
        this.notificationService.sendMessage({
          message: this.translate.instant('design.customImage.calibrationSuccess') || 'Image scaled to map',
          type: NotificationType.success,
          positionClass: 'toast-bottom-left',
          timeOut: 3000
        });
      })
    ),
    { dispatch: false }
  );

  replaceCustomImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCustomImageActions.ReplaceCustomImage),
      withLatestFrom(this.store.select(fromCustomImageSelectors.selectCustomImage)),
      switchMap(([{ file }, existingImage]) => {
        // Note: According to PRD, "The drawn roof or panels will stay as is" when replacing image
        // We only replace the custom image, designs remain intact
        
        // Upload the new image with the same source type as before
        const uploadSourceType = existingImage?.uploadSourceType || 'GoogleMap';
        return of(fromCustomImageActions.UploadCustomImage({ 
          file, 
          uploadSourceType 
        }));
      })
    )
  );

  // Persist to localStorage when confirmation dialog preference changes
  setDontShowConfirmationAgain$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCustomImageActions.SetDontShowConfirmationAgain),
      tap(({ dontShow }) => {
        localStorage.setItem('customImage_dontShowConfirmation', JSON.stringify(dontShow));
      })
    ),
    { dispatch: false }
  );

  // Exit edit mode when selection is cleared
  exitEditModeOnClearSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUiActions.ClearSelectionSuccess),
      withLatestFrom(this.store.select(fromCustomImageSelectors.selectIsEditMode)),
      filter(([_, isEditMode]) => isEditMode === true),
      map(() => fromCustomImageActions.ExitEditMode())
    )
  );

  // Show notification when edit mode is cancelled
  cancelEditModeNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCustomImageActions.CancelEditMode),
      tap(() => {
        this.notificationService.sendMessage({
          message: this.translate.instant('design.customImage.changesReverted') || 'Image changes have been reverted.',
          type: NotificationType.warning,
          positionClass: 'toast-bottom-left',
          timeOut: 2500
        });
      })
    ),
    { dispatch: false }
  );

  // After cancel edit mode, trigger merge for pre-existing images (to restore merged state)
  mergeAfterCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromCustomImageActions.CancelEditMode),
      withLatestFrom(
        this.store.select(fromCustomImageSelectors.selectIsNewImage),
        this.store.select(fromCustomImageSelectors.selectCustomImageExists)
      ),
      // Only trigger merge if it's not a new image and the image still exists (wasn't deleted)
      filter(([_, isNewImage, imageExists]) => !isNewImage && imageExists),
      map(() => fromCustomImageActions.MergeCustomImageWithGround())
    )
  );
}
